import { Check, Delete } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../../assets/scss/components/NumericKeypad.module.scss';

// Format chuỗi số thô để hiển thị: phần nguyên ngăn cách hàng nghìn bằng ",",
// phần thập phân (nếu có) giữ nguyên sau dấu ".". Hoạt động chung cho cả số
// nguyên (không có dấu ".") lẫn số thập phân.
function formatRaw(raw) {
    if (raw === '' || raw === '-') return raw;
    const [intPart = '', decPart] = raw.split('.');
    const intNum = intPart.replace(/\D/g, '');
    const intFmt = intNum ? Number(intNum).toLocaleString('en-US') : '0';
    return decPart !== undefined ? `${intFmt}.${decPart}` : intFmt;
}

/**
 * Bàn phím số ảo dạng modal — người dùng bấm chuột để nhập số/tiền,
 * không cần gõ bàn phím vật lý.
 *
 * @param {boolean}  open       Hiển thị modal hay không.
 * @param {string}   title      Tiêu đề (vd: "Giá bán").
 * @param {string|number} value Giá trị khởi tạo (thô).
 * @param {'money'|'integer'|'decimal'} mode  Kiểu nhập. Mặc định 'money'.
 *        'integer' luôn ép số nguyên (bỏ qua allowDecimal).
 * @param {boolean}  allowDecimal  Cho nhập thập phân hay không. Mặc định true;
 *        đặt false để ẩn nút "." và chặn thập phân (mode='integer' luôn chặn).
 * @param {number}   maxDecimals  Số chữ số thập phân tối đa. Mặc định 2.
 * @param {string}   suffix     Đơn vị hiển thị (vd: "đ", "phút").
 * @param {(raw: string) => void} onConfirm  Trả về chuỗi số thô khi bấm Xong.
 * @param {() => void} onCancel  Đóng/huỷ.
 */
export default function NumericKeypad({
    open,
    title = 'Nhập số',
    value = '',
    mode = 'money',
    allowDecimal = true,
    maxDecimals = 2,
    suffix,
    onConfirm,
    onCancel,
    overlayClassName,
}) {
    const [raw, setRaw] = useState('');

    // Số nguyên (mode='integer') luôn chặn thập phân, các kiểu khác theo allowDecimal.
    const decimalsOn = allowDecimal && mode !== 'integer';

    // Mỗi lần mở lại, nạp giá trị hiện tại (chỉ giữ ký tự hợp lệ).
    useEffect(() => {
        if (!open) return;
        const init = String(value ?? '');
        if (decimalsOn) {
            setRaw(init.replace(',', '.').replace(/[^\d.]/g, ''));
        } else {
            setRaw(init.replace(/\D/g, ''));
        }
    }, [open, value, decimalsOn]);

    const press = useCallback(
        (digit) => {
            setRaw((prev) => {
                if (digit === '.') {
                    if (!decimalsOn || prev.includes('.')) return prev;
                    return prev === '' ? '0.' : `${prev}.`;
                }
                // chặn vượt số chữ số thập phân
                if (decimalsOn && prev.includes('.')) {
                    const dec = prev.split('.')[1] || '';
                    if (dec.length + digit.length > maxDecimals) return prev;
                }
                // tránh số 0 thừa ở đầu (giữ "0." khi đang nhập thập phân)
                if (prev === '0') return digit.replace(/^0+/, '') || '0';
                return prev + digit;
            });
        },
        [decimalsOn, maxDecimals]
    );

    const backspace = useCallback(() => setRaw((p) => p.slice(0, -1)), []);
    const clearAll = useCallback(() => setRaw(''), []);

    const confirm = useCallback(() => {
        // raw chỉ chứa chữ số và tối đa một dấu ".", chỉ cần bỏ dấu "." thừa ở cuối.
        let out = raw;
        if (out.endsWith('.')) out = out.slice(0, -1);
        onConfirm?.(out);
    }, [raw, onConfirm]);

    // Hỗ trợ gõ bàn phím vật lý song song + Esc để đóng.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') return onCancel?.();
            if (e.key === 'Enter') return confirm();
            if (e.key === 'Backspace') return backspace();
            if (e.key >= '0' && e.key <= '9') return press(e.key);
            if ((e.key === '.' || e.key === ',') && decimalsOn) return press('.');
        };
        window.addEventListener('keydown', onKey);
        // khoá cuộn nền
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, decimalsOn, press, backspace, confirm, onCancel]);

    if (!open) return null;

    const display = formatRaw(raw);

    // Render qua portal ra document.body để overlay thoát khỏi mọi stacking
    // context (vd cột position:sticky), phủ toàn màn hình thay vì bị giam trong cột.
    return createPortal(
        <div className={overlayClassName || styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.display}>
                    <span className={styles.title}>{title}</span>
                    <span className={styles.value}>
                        {display === '' ? <span className={styles.placeholder}>0</span> : display}
                        {suffix && display !== '' && <span className={styles.suffix}> {suffix}</span>}
                    </span>
                </div>

                <div className={styles.grid}>
                    {['1', '2', '3'].map((d) => (
                        <button key={d} type="button" className={styles.key} onClick={() => press(d)}>
                            {d}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={styles.key}
                        onClick={() => press('.')}
                        disabled={!decimalsOn}
                        style={!decimalsOn ? { visibility: 'hidden' } : undefined}
                    >
                        .
                    </button>

                    {['4', '5', '6'].map((d) => (
                        <button key={d} type="button" className={styles.key} onClick={() => press(d)}>
                            {d}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`${styles.key} ${styles.keyAction}`}
                        onClick={clearAll}
                        title="Xoá hết"
                    >
                        AC
                    </button>

                    {['7', '8', '9'].map((d) => (
                        <button key={d} type="button" className={styles.key} onClick={() => press(d)}>
                            {d}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`${styles.key} ${styles.keyAction}`}
                        onClick={backspace}
                        title="Xoá 1 ký tự"
                    >
                        <Delete size={22} />
                    </button>

                    <button type="button" className={styles.key} onClick={() => press('0')}>
                        0
                    </button>
                    <button type="button" className={styles.key} onClick={() => press('000')}>
                        000
                    </button>
                    <button
                        type="button"
                        className={`${styles.key} ${styles.keyConfirm}`}
                        style={{ gridColumn: 'span 2' }}
                        onClick={confirm}
                    >
                        <Check size={26} />
                    </button>
                </div>

                <div className={styles.footer}>
                    <button type="button" className={`${styles.btn} ${styles.btnCancel}`} onClick={onCancel}>
                        Bỏ qua
                    </button>
                    <button type="button" className={`${styles.btn} ${styles.btnDone}`} onClick={confirm}>
                        Xong
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
